import React from 'react';
import { Chip } from '@mui/material';
import AccessAlarmOutlinedIcon from '@mui/icons-material/AccessAlarmOutlined';
import useSessionCountdown from '../hooks/useSessionCountdown';
import {
  getLastActiveFromUser,
  getTabWindowSeconds,
} from '../utils/sessionWindow';
import useInterveneTheme from '../hooks/useInterveneTheme';
import useInterveneSx from '../hooks/useInterveneSx';

/**
 * Tab-aware session countdown (15 Min → 15m window, 24 HRS/Intervene → 24h window).
 */
export default function SessionCountdownBadge({ user, tabValue, dense = false }) {
  const { chat, status } = useInterveneTheme();
  const listStyles = useInterveneSx().userListItem;
  const lastActive = getLastActiveFromUser(user);
  const windowSeconds = getTabWindowSeconds(tabValue);
  const { formatted, isExpired } = useSessionCountdown(lastActive, windowSeconds);

  if (!lastActive) return null;

  const isLiveSession = user?.intervene && user?.session_start;

  if (dense) {
    return (
      <Chip
        icon={
          !isExpired ? (
            <AccessAlarmOutlinedIcon sx={{ fontSize: '12px !important' }} />
          ) : undefined
        }
        label={isExpired ? 'Expired' : formatted}
        size="small"
        variant="outlined"
        sx={{
          ...listStyles.expiredChip,
          height: listStyles.expiredChip?.height || 20,
          fontSize: listStyles.expiredChip?.fontSize || '10px',
          fontWeight: 500,
          minWidth: isExpired ? "75px" : '95px',
          fontVariantNumeric: 'tabular-nums',
          // Legacy list countdown uses the same red outline chip as Expired.
          borderColor: status.expiredBorder,
          color: status.expired,
          bgcolor: isExpired ? status.expiredBg : 'transparent',
          '& .MuiChip-label': {
            px: 0.75,
            whiteSpace: 'nowrap',
            fontVariantNumeric: 'tabular-nums',
          },
          '& .MuiChip-icon': {
            color: 'inherit',
            ml: 0.5,
            mr: -0.25,
            flexShrink: 0,
          },
          '@media (max-width:768px)': {
            minWidth: isExpired ? '75px' : '85px',
          },
        }}
      />
    );
  }

  return (
    <Chip
      icon={!isExpired ? <AccessAlarmOutlinedIcon sx={{ fontSize: '14px !important' }} /> : undefined}
      label={isExpired ? 'Expired' : formatted}
      size="small"
      variant="outlined"
      sx={{
        height: 22,
        fontSize: '0.65rem',
        fontWeight: 600,
        minWidth: isExpired ? "75px" : '95px',
        fontVariantNumeric: 'tabular-nums',
        '& .MuiChip-label': {
          whiteSpace: 'nowrap',
          fontVariantNumeric: 'tabular-nums',
        },
        borderRadius: listStyles.expiredChip?.borderRadius || '999px',
        borderColor: isExpired
          ? status.expiredBorder
          : isLiveSession
            ? chat.success
            : chat.primaryBorder,
        color: isExpired ? status.expired : isLiveSession ? chat.success : chat.primary,
        bgcolor: isExpired ? status.expiredBg : chat.primarySurface,
        '& .MuiChip-icon': {
          color: 'inherit',
          ml: 0.5,
           flexShrink: 0,
        },
        '@media (max-width:768px)': {
            minWidth: isExpired ? '75px' : '85px',
          },
      }}
    />
  );
}
